/**
 * Created by Jerker Nord on 2020-08-27.
 */
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {TimelineLite, TweenLite} from "gsap";
import {BitmapTextStyleOptions} from "@nolimitcity/slot-game/bin/core/text/BitmapTextStyleOptions";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {MonkeysGoldGameConfig} from "../../MonkeysGoldGameConfig";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {ParsedGameData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedGameData";
import {IBetWayWinData, IBetWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {BitmapTextStyleData} from "../../symbols/MonkeysGoldBitmapText";

export interface IMonkeysGoldBetWayWinData extends IBetWayWinData {
    length:number;
    reelWinPositions:boolean[][];
    symbolCount:number;
}

export class MonkeysGoldWaysWonGFXText extends BaseView {

    private readonly _waysWonNumberStyle:BitmapTextStyleData = {
        fontName : "XpaysCountup",
        fontSize : 50,
        tint : 0xffffff
    };
    private readonly _waysAmountNumberStyle:BitmapTextStyleData = {

        fontName : "NumbersWinCountUp",
        fontSize : 40,
        tint : 0xffffff
    };
    private readonly _wayWonTexts:string[] = ["xPaysText"];
    private readonly _fadeInDuration:number = 0.5;

    private _waysWonContainer:PIXI.Container;
    private _waysWonNumber:PIXI.BitmapText;
    private _waysAmountNumber:PIXI.BitmapText;
    private _xPaysImage:PIXI.Sprite;
    private _currentTimeline:TimelineLite;
    private _fadeOutTimeline:TimelineLite;
    private _winWaysTotal:number;

    constructor() {
        super();
    }

    protected initAnimations():void {
        const layer:ScalableLayer = StageManager.getLayer(MonkeysGoldGameConfig.instance.LAYERS.WIN_COUNT_UP.name);
        layer.addChild(this);

        this._waysWonContainer = new PIXI.Container();
        this.addChild(this._waysWonContainer);

        this._waysWonNumber = new PIXI.BitmapText("", this._waysWonNumberStyle);
        (<PIXI.Point>this._waysWonNumber.anchor).set(0, 0);
        this._waysWonNumber.y = -12;
        this._waysWonNumber.x = 69;
        this._waysWonNumber.alpha = 0;
        this._waysWonContainer.addChild(this._waysWonNumber);

        this._waysAmountNumber = new PIXI.BitmapText("", this._waysAmountNumberStyle);
        (<PIXI.Point>this._waysAmountNumber.anchor).set(0, 0);
        this._waysAmountNumber.alpha = 0;
        this._waysWonContainer.addChild(this._waysAmountNumber);

        this._xPaysImage = new PIXI.Sprite(GameResources.getTextures("xPaysText")[0]);
        this._xPaysImage.alpha = 0;
        this._waysWonContainer.addChild(this._xPaysImage);

        this._waysWonContainer.visible = false;
    }

    protected addEventListeners():void {
        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event:GameEvent) => this.onGameDataParsed(event.params[0]));
    }

    protected onGameDataParsed(data:ParsedGameData):void {
        this._winWaysTotal = 0;
        if(data.betWins) {
            data.betWins.forEach((betWayWin:IBetWinData) => {
                this._winWaysTotal += (<IMonkeysGoldBetWayWinData>betWayWin).symbolCount;
            });
        }
    }

    public showWinWaysWon(winWaysWon:number, winAmount:number, animate:boolean):void {
        winWaysWon = winWaysWon != undefined ? winWaysWon : this._winWaysTotal;
        animate = animate != undefined ? animate : true;
        const wayWonText:string = this._wayWonTexts[0];

        this._currentTimeline = new TimelineLite();
        this._currentTimeline.add([
            new TweenLite(this._waysWonNumber, animate ? this._fadeInDuration : 0, {alpha : 1}),
            new TweenLite(this._waysAmountNumber, animate ? this._fadeInDuration : 0, {alpha : 1}),
            new TweenLite(this._xPaysImage, animate ? this._fadeInDuration : 0, {alpha : 1}),
            () => {
                this._waysWonNumber.text = "" + winWaysWon;
                this._waysAmountNumber.text = SlotGame.api.currency.format(winAmount);
                this.updatePositions();
                this._waysWonContainer.visible = true;
            }
        ]);
    }

    private updatePositions():void {
        this._waysWonNumber.updateTransform();
        this._waysAmountNumber.updateTransform();
        const totalWidth:number = this._waysWonNumber.width + this._xPaysImage.width;
        this._waysAmountNumber.position.y = -82;
        this._waysAmountNumber.position.x = (this._xPaysImage.width / 2) - (this._waysAmountNumber.width / 2) - 10;
        this._waysWonContainer.updateTransform();
        if((this.position.y + this._waysWonContainer.position.y + this._waysAmountNumber.position.y) > 540) {
            this.position.y -= 20;
        }
        this._waysWonContainer.updateTransform();
    }

    public abort(isAborted:boolean, isBigWin:boolean):void {
        if(this._currentTimeline && this._currentTimeline.isActive()) {
            this._currentTimeline.pause();
            this._currentTimeline.kill();
        }
        if(this._fadeOutTimeline && this._fadeOutTimeline.isActive()) {
            this._fadeOutTimeline.pause();
            this._fadeOutTimeline.kill();
        }
        this._waysWonNumber.alpha = 0;
        this._waysAmountNumber.alpha = 0;
        this._xPaysImage.alpha = 0;
        this._waysWonContainer.visible = false;
    }

    public fadeOut():void {
        this._fadeOutTimeline = new TimelineLite();
        this._fadeOutTimeline.add([
            new TweenLite(this._waysWonNumber, 0.3, {alpha : 0}),
            new TweenLite(this._waysAmountNumber, 0.3, {alpha : 0}),
            new TweenLite(this._xPaysImage, 0.3, {alpha : 0})
        ]);
    }
}